export const normalize = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
export const skuKey = value => normalize(value).replace(/ /g, '');
export const isSkuLike = value => /[a-z0-9][-_.][a-z0-9]?/i.test(value) || (!/\s/.test(value) && /\d/.test(value));
const matches = (title, query) => {
  const words = normalize(title).split(' '), terms = normalize(query).split(' ');
  return terms.every((term, i) => words.some(word => i === terms.length - 1 ? word.startsWith(term) : word === term));
};
export function rankProducts(products, query) {
  const seen = new Set(), q = normalize(query), key = skuKey(query), terms = q.split(' ');
  return products.filter(p => p && p.id && !seen.has(p.id) && seen.add(p.id)).map((p, index) => {
    const variants = (p.variants || []).filter(v => v.sku && key.length >= 2 && skuKey(v.sku).startsWith(key));
    variants.sort((a, b) => Number(skuKey(b.sku) === key) - Number(skuKey(a.sku) === key) || Number(b.available) - Number(a.available));
    const variant = variants[0], title = normalize(p.title);
    let score = variant ? (skuKey(variant.sku) === key ? 10000 : 9000) : title === q ? 8000 : title.startsWith(q) ? 7000 : matches(p.title, query) ? 6000 : 0;
    if (!variant) score -= terms.reduce((sum, word) => sum + Math.max(0, title.indexOf(word)), 0);
    if (!(variant?.available ?? p.available)) score -= 50;
    return {...p, matchedVariant: variant, score, sourceIndex: index};
  }).sort((a, b) => b.score - a.score || a.sourceIndex - b.sourceIndex);
}
export function makeSuggestions(query, data, config) {
  const q = normalize(query), key = skuKey(query), output = [], seen = new Set();
  const add = (text, kind) => {
    const normalized = normalize(text);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized); output.push({text, kind});
  };
  if (key.length >= 2) {
    const skus = (data.products || []).flatMap(p => p.variants || []).filter(v => v.sku && skuKey(v.sku).startsWith(key));
    skus.sort((a,b) => Number(skuKey(b.sku) === key) - Number(skuKey(a.sku) === key));
    skus.forEach(v => add(v.sku, 'SKU'));
  }
  (config.vendors || []).filter(v => normalize(v).startsWith(q) && normalize(v) !== q).forEach(v => {
    const display = v === v.toUpperCase() && v.includes(' ') ? v.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) : v;
    add(display, 'Brand');
  });
  const curated = (config.collections || []).filter(c => matches(c.title, query) && c.count > 0);
  if (/^solar panels?$/.test(q)) curated.sort((a,b) => Number(/portable|rigid|flexible/i.test(b.title)) - Number(/portable|rigid|flexible/i.test(a.title)));
  curated.slice(0, 3).forEach(c => add(c.title, 'Category'));
  (data.collections || []).filter(c => c.count > 0 && normalize(c.title) !== q).slice(0, 3).forEach(c => add(c.title, 'Category'));
  (data.queries || []).filter(item => normalize(item.text) !== q).forEach(item => add(item.text, 'Search'));
  return output.slice(0, 4);
}
// Share recent responses across both layouts and page visits within this shopping session.
const CACHE_TTL = 120000, stores = new Map();
export function canonicalQuery(query, vendors = []) {
  const q=normalize(query), candidates=vendors.filter(v=>normalize(v).startsWith(q));
  return q.length>=2&&candidates.length===1 ? candidates[0] : query;
}
export function matchingProducts(products, query) {
  const key=skuKey(query);
  return products.filter(p=>matches([p.title,p.vendor,...(p.variants||[]).map(v=>v.title)].join(' '),query)||(key.length>=2&&(p.variants||[]).some(v=>v.sku&&skuKey(v.sku).startsWith(key))));
}
function searchStore(key, persist) {
  if(stores.has(key))return stores.get(key);
  const store={cache:new Map(),pending:new Map(),watchers:new Set(),storageKey:persist?'gg-search-v1:'+key:null};
  if(store.storageKey)try{
    const saved=JSON.parse(sessionStorage.getItem(store.storageKey)||'[]');
    for(const [query,entry]of saved)if(Date.now()-entry.at<CACHE_TTL&&entry.complete&&Array.isArray(entry.data?.products))store.cache.set(query,entry);
  }catch{}
  stores.set(key,store);return store;
}
function remember(store,key,data,complete) {
  store.cache.delete(key);store.cache.set(key,{at:Date.now(),data,complete});
  if(store.cache.size>30)store.cache.delete(store.cache.keys().next().value);
  if(complete&&store.storageKey){
    clearTimeout(store.saveTimer);store.saveTimer=setTimeout(()=>{
      try{
        const entries=[...store.cache].filter(([,e])=>e.complete&&Date.now()-e.at<CACHE_TTL).slice(-20);
        let json=JSON.stringify(entries);while(json.length>250000&&entries.length){entries.shift();json=JSON.stringify(entries);}
        sessionStorage.setItem(store.storageKey,json);
      }catch{}
    },100);
  }
}
function knownProducts(store,query){
  const seen=new Set(),products=[];
  for(const entry of [...store.cache.values()].reverse())if(Date.now()-entry.at<CACHE_TTL)for(const p of entry.data.products)if(!seen.has(p.id)){seen.add(p.id);products.push(p);}
  return matchingProducts(products,query);
}
function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}
function arrow(completion = false) {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('fill', 'none'); svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS(svg.namespaceURI, 'path');
  path.setAttribute('d', completion ? 'M17 17 7 7m0 8V7h8' : 'M5 12h14m-6-6 6 6-6 6');
  path.setAttribute('stroke', 'currentColor'); path.setAttribute('stroke-linecap', 'round'); path.setAttribute('stroke-linejoin', 'round');
  svg.append(path); return svg;
}
function safeUrl(value, image = false) {
  if (!value) return '';
  try { const url = new URL(value, location.origin); return ['http:', 'https:'].includes(url.protocol) && (image || url.origin === location.origin) ? url.href : ''; } catch { return ''; }
}
if (typeof customElements !== 'undefined' && !customElements.get('gg-mobile-search')) {
  customElements.define('gg-mobile-search', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.ready = true;
      this.desktop = this.hasAttribute('data-desktop');
      this.section = this.closest('.shopify-section');
      this.form = this.desktop ? this.parentElement.querySelector('[data-gg-search-form]') : this.querySelector('form');
      if(this.desktop){
        // Preserve the centered field and native form while replacing its old input listeners.
        const oldInput=this.form.querySelector('[data-gg-search-input]');
        this.input=oldInput.cloneNode(true);oldInput.replaceWith(this.input);
        this.input.placeholder='What are you searching for?';
        this.input.setAttribute('aria-label','What are you searching for?');
        this.input.setAttribute('aria-controls',this.dialog.id);
        this.input.setAttribute('aria-haspopup','dialog');
        this.input.setAttribute('aria-autocomplete','list');
        this.input.setAttribute('role','combobox');
        this.input.setAttribute('autocapitalize','none');this.input.spellcheck=false;
        this.form.classList.remove('is-open');
      }else this.input = this.querySelector('.gg-smart-input');
      this.opener = this.desktop ? this.input : this.querySelector('.gg-smart-open');
      this.content = this.querySelector('.gg-smart-content');
      this.status = this.querySelector('.gg-smart-live');
      this.clear = this.querySelector('.gg-smart-clear');
      try { this.config = JSON.parse(this.querySelector('[data-gg-smart-config]').textContent); } catch { this.config = {vendors:[],collections:[]}; }
      this.media = matchMedia('(max-width: 1149px)');
      const currency=window.Shopify?.currency?.active,country=window.Shopify?.country;
      this.store=searchStore([this.dataset.suggestUrl,document.documentElement.lang,country,currency].join('|'),Boolean(currency&&country));
      this.cache=this.store.cache;this.sequence=0;
      this.poolChanged=()=>{
        if(!this.awaiting||!this.dialog.open)return;
        const query=this.input.value.trim(),products=knownProducts(this.store,query);
        if(products.length)this.render(query,{products,collections:[],queries:[]},'refreshing');
      };
      this.store.watchers.add(this.poolChanged);
      this.listeners = new AbortController(); const options = {signal: this.listeners.signal};
      // A body-level dialog stays above the sticky header and other floating storefront widgets.
      document.body.append(this.dialog);
      this.opener.hidden = false; this.dataset.ready = '';
      if(this.desktop){
        this.section.dataset.ggDesktopReady='';this.opener.setAttribute('aria-expanded','false');
        this.input.addEventListener('focus',()=>this.open(),options);
        this.input.addEventListener('click',()=>this.open(),options);
        this.form.addEventListener('keydown',event=>this.onKey(event),options);
        const outside=event=>{if(!this.form.contains(event.target)&&!this.dialog.contains(event.target))this.close(false);};
        document.addEventListener('pointerdown',outside,options);
        document.addEventListener('focusin',outside,options);
        window.addEventListener('resize',()=>this.fitViewport(),options);
        document.addEventListener('scroll',event=>{if(!this.dialog.contains(event.target))this.close(false);},{...options,capture:true});
      }else{
        this.section.dataset.ggSmartReady = '';
        this.opener.addEventListener('click', () => this.open(), options);
        this.dialog.querySelector('.gg-smart-close').addEventListener('click', () => this.close(), options);
      }
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, options);
      this.dialog.addEventListener('close', () => this.finishClose(), options);
      this.clear?.addEventListener('click', () => { this.input.value = ''; this.update(); this.input.focus(); }, options);
      this.input.addEventListener('input', () => { if (!this.composing) {if(this.desktop&&!this.dialog.open)this.open();else this.update();} }, options);
      this.input.addEventListener('compositionstart', () => { this.composing = true; this.cancelSearch(); }, options);
      this.input.addEventListener('compositionend', () => { this.composing = false; this.update(); }, options);
      this.dialog.addEventListener('keydown', event => this.onKey(event), options);
      this.form.addEventListener('submit', event => {
        const query = this.input.value.trim();
        if (!query) { event.preventDefault(); this.input.focus(); }
        else this.input.value = query;
      }, options);
      this.media.addEventListener('change', () => { if (this.desktop ? this.media.matches : !this.media.matches) this.close(false); }, options);
      window.visualViewport?.addEventListener('resize', () => this.fitViewport(), options);
      window.addEventListener('pageshow', () => { if (this.dialog.open) this.close(false); }, options);
      this.renderEmpty();
      // Let the existing header recompute its height after removing the search row.
      window.dispatchEvent(new Event('resize'));
    }
    disconnectedCallback() {
      queueMicrotask(()=>{
      if(this.isConnected)return; // The existing header moves the desktop field at its mobile breakpoint.
      this.cancelSearch(); this.close(false); this.finishClose(); this.listeners?.abort(); this.dialog?.remove();
      if (this.section) {if(this.desktop)delete this.section.dataset.ggDesktopReady;else delete this.section.dataset.ggSmartReady;}
      this.store?.watchers.delete(this.poolChanged);
      this.ready = false;
      });
    }
    open() {
      if(this.suppressOpen)return;
      if(this.desktop){
        if(this.media.matches||this.dialog.open)return;
        this.dialog.show();this.opener.setAttribute('aria-expanded','true');this.fitViewport();
        this.input.focus({preventScroll:true});this.update(true);return;
      }
      if (!this.media.matches || this.dialog.open) return;
      const menu = this.section.querySelector('[data-gg-mobile-drawer].is-open');
      menu?.querySelector('[data-gg-mobile-close]')?.click();
      this.savedScroll = window.scrollY;
      this.savedBody = {};
      for (const key of ['position','top','left','right','width','overflow']) this.savedBody[key] = document.body.style[key];
      Object.assign(document.body.style, {position:'fixed',top:`-${this.savedScroll}px`,left:'0',right:'0',width:'100%',overflow:'hidden'});
      this.dialog.showModal(); this.opener.setAttribute('aria-expanded','true'); this.fitViewport();
      this.input.focus({preventScroll:true});
      this.update(true);
    }
    close(restoreFocus = true) { this.restoreFocus = restoreFocus; if (this.dialog?.open) this.dialog.close(); }
    finishClose() {
      this.cancelSearch();
      if (this.savedBody) {
        Object.assign(document.body.style, this.savedBody); this.savedBody = null;
        window.scrollTo({top:this.savedScroll || 0,behavior:'instant'});
      }
      this.opener?.setAttribute('aria-expanded','false');
      if (!this.desktop && this.restoreFocus !== false && this.media?.matches && this.opener?.isConnected) this.opener.focus({preventScroll:true});
      if (this.desktop && this.restoreFocus !== false && !this.media?.matches && this.opener?.isConnected) {
        this.suppressOpen=true;this.opener.focus({preventScroll:true});this.suppressOpen=false;
      }
      this.restoreFocus = true;
    }
    fitViewport() {
      if(!this.dialog.open)return;
      if(this.desktop){
        const rect=this.form.getBoundingClientRect(),width=Math.min(Math.max(rect.width,600),innerWidth-32),top=rect.bottom+8;
        Object.assign(this.dialog.style,{left:`${Math.max(16,Math.min(rect.left+(rect.width-width)/2,innerWidth-width-16))}px`,top:`${top}px`,width:`${width}px`,maxHeight:`${Math.max(120,innerHeight-top-16)}px`});
      }else this.dialog.style.setProperty('--gg-search-height', `${window.visualViewport?.height || innerHeight}px`);
    }
    cancelSearch() { clearTimeout(this.timer); this.unsubscribe?.(); this.unsubscribe=null; this.awaiting=false; this.sequence++; }
    searchUrl(query) {
      const url = new URL(this.dataset.searchUrl || '/search', location.origin);
      url.searchParams.set('type','product'); url.searchParams.set('q',query); url.searchParams.set('options[prefix]','last');
      return url.href;
    }
    group(title) { const group=element('section','gg-smart-group');group.append(element('h3','gg-smart-label',title));return group; }
    renderEmpty() {
      this.content.replaceChildren(); this.content.removeAttribute('aria-busy'); this.status.textContent = '';
      const group = this.group('Explore categories'), chips = element('div','gg-smart-chips');
      (this.config.collections || []).slice(0,6).forEach(c => {
        const button=element('button','gg-smart-chip',c.title);button.type='button';button.addEventListener('click',()=>this.choose(c.title));chips.append(button);
      });
      group.append(chips);this.content.append(group);
    }
    choose(query) { this.input.value=query;this.input.focus({preventScroll:true});this.update(true);this.dialog.querySelector('.gg-smart-scroll').scrollTop=0; }
    update(immediate = false) {
      this.cancelSearch();const version=this.sequence,query=this.input.value.trim();if(this.clear)this.clear.hidden=!query;
      if(!query){this.renderEmpty();return;}
      const key=canonicalQuery(query,this.config.vendors).toLowerCase(),cached=this.cache.get(key);
      if(cached&&cached.complete&&Date.now()-cached.at<CACHE_TTL){this.render(query,cached.data);return;}
      const products=knownProducts(this.store,query);
      this.awaiting=query.length>=2;
      this.render(query,{products,collections:[],queries:[]},this.awaiting?(products.length?'refreshing':'loading'):'short');
      if(!this.awaiting)return;
      // Start immediately, with a short throttle only during a burst of different queries.
      const delay=immediate?0:Math.max(0,60-(performance.now()-(this.lastDispatch||0)));
      if(!delay)this.search(query,version);else this.timer=setTimeout(()=>this.search(query,version),delay);
    }
    async request(query, fields, signal, skuOnly = false) {
      const url=new URL(this.dataset.suggestUrl || '/search/suggest',location.origin);
      for(const [key,value]of Object.entries({q:query,'resources[type]':skuOnly?'product':'product,collection,query','resources[limit]':'10','resources[limit_scope]':'each','resources[options][fields]':fields,'resources[options][unavailable_products]':'last',section_id:'gg-smart-search-results'}))url.searchParams.set(key,value);
      const response=await fetch(url,{signal,headers:{Accept:'text/html'}});
      if(!response.ok)throw new Error(`Search ${response.status}`);
      const documentResult=new DOMParser().parseFromString(await response.text(),'text/html');
      const payload=documentResult.querySelector('[data-gg-smart-results]');
      if(!payload)throw new Error('Search response unavailable');
      const data=JSON.parse(payload.textContent);
      if(!Array.isArray(data.products)||!Array.isArray(data.queries)||!Array.isArray(data.collections))throw new Error('Invalid search response');
      return data;
    }
    search(query,version) {
      if(version!==this.sequence||!this.dialog.open)return;
      this.lastDispatch=performance.now();
      const requestQuery=canonicalQuery(query,this.config.vendors),key=requestQuery.toLowerCase(),store=this.store;
      const receive=(data,error=false)=>{
        if(version!==this.sequence||!this.dialog.open)return;
        this.awaiting=false;
        const products=data?.products||knownProducts(store,query);
        this.render(query,data||{products,collections:[],queries:[]},error&&!products.length?'error':'ready');
      };
      let task=store.pending.get(key);
      if(task){task.listeners.add(receive);this.unsubscribe=()=>task.listeners.delete(receive);if(task.data)receive(task.data);return;}
      // Older requests may still supply useful matches while the visitor keeps typing.
      // Bound their number so fast typing cannot create an unlimited request queue.
      if(store.pending.size>=3){const oldest=store.pending.values().next().value;oldest.controller.abort();store.pending.delete(oldest.key);}
      task={key,controller:new AbortController(),listeners:new Set([receive]),parts:[],data:null};
      store.pending.set(key,task);this.unsubscribe=()=>task.listeners.delete(receive);
      const timeout=setTimeout(()=>task.controller.abort(),4000);
      const publish=()=>{
        const primary=task.parts[0]||{queries:[],collections:[],products:[]};
        task.data={...primary,products:task.parts.filter(Boolean).flatMap(part=>part.products)};
        remember(store,key,task.data,false);
        task.listeners.forEach(listener=>listener(task.data));
        store.watchers.forEach(watcher=>watcher());
      };
      const requests=[this.request(requestQuery,'title,product_type,vendor,variants.title,variants.sku',task.controller.signal).then(data=>{task.parts[0]=data;publish();})];
      if(isSkuLike(requestQuery))requests.push(this.request(requestQuery,'variants.sku',task.controller.signal,true).then(data=>{task.parts[1]=data;publish();}));
      // Each response renders immediately; completion is only used for cache bookkeeping.
      Promise.allSettled(requests).then(()=>{
        clearTimeout(timeout);
        if(store.pending.get(key)===task)store.pending.delete(key);
        if(task.data)remember(store,key,task.data,true);else task.listeners.forEach(listener=>listener(null,true));
        task.listeners.clear();
      });
    }
    render(query,data,state='ready') {
      const suggestions=makeSuggestions(query,data,this.config),products=rankProducts(data.products||[],query).slice(0,4);
      const active=document.activeElement,focusedQuery=this.content.contains(active)?active.dataset.query:null,focusedHref=this.content.contains(active)?active.getAttribute('href'):null,focusedProduct=this.content.contains(active)?active.dataset.productId:null;
      this.content.replaceChildren();this.content.setAttribute('aria-busy',String(state==='loading'||state==='refreshing'));
      if(suggestions.length){
        const group=this.group('Suggested searches'),list=element('ul','gg-smart-suggestions');
        suggestions.forEach(s=>{
          const li=element('li'),button=element('button','gg-smart-suggestion');button.type='button';button.dataset.ggSearchResult='';button.dataset.query=s.text;
          const label=element('span');const at=s.text.toLowerCase().indexOf(query.toLowerCase());
          if(at>=0){label.append(document.createTextNode(s.text.slice(0,at+query.length)),element('strong','',s.text.slice(at+query.length)));}else label.textContent=s.text;
          button.append(label,element('small','',s.kind),arrow(true));button.addEventListener('click',()=>this.choose(s.text));li.append(button);list.append(li);
        });group.append(list);this.content.append(group);
      }
      if(products.length){
        const group=this.group('Products'),list=element('div','gg-smart-products');
        products.forEach(p=>{
          const variant=p.matchedVariant,href=safeUrl(variant?.url||p.url);if(!href)return;
          const link=element('a','gg-smart-product');link.href=href;link.dataset.ggSearchResult='';link.dataset.productId=String(p.id);if(variant)link.dataset.sku=variant.sku;
          const imageWrap=element('span','gg-smart-product-image'),src=safeUrl(variant?.image||p.image,true);
          if(src){const img=element('img');img.src=src;img.alt='';img.width=66;img.height=66;img.loading='eager';img.decoding='async';imageWrap.append(img);}
          const copy=element('span','gg-smart-product-copy');copy.append(element('span','gg-smart-product-title',p.title));
          const available=variant?.available??p.available;
          copy.append(element('span','gg-smart-product-meta',[variant?`SKU ${variant.sku}`:p.vendor,available?'':'Sold out'].filter(Boolean).join(' · ')));
          copy.append(element('span','gg-smart-product-price',`${!variant&&p.varies?'From ':''}${variant?.price||p.price}`));link.append(imageWrap,copy);list.append(link);
        });group.append(list);this.content.append(group);
      }else{
        const message=state==='loading'?'Searching products…':state==='error'?'Suggestions are temporarily unavailable. You can still search all products.':state==='short'?'Keep typing to see matching products.':`No products found for “${query}”. Try a brand, product name, or SKU.`;
        this.content.append(element('p','gg-smart-message',message));
        if(state==='error'){const retry=element('button','gg-smart-retry','Try again');retry.type='button';retry.addEventListener('click',()=>this.update(true));this.content.append(retry);}
      }
      const all=element('a','gg-smart-all');all.href=this.searchUrl(query);all.append(element('span','',`View all results for “${query}”`),arrow());this.content.append(all);
      if(focusedQuery||focusedHref||focusedProduct){const replacement=[...this.content.querySelectorAll('[data-gg-search-result],.gg-smart-all')].find(n=>focusedQuery?n.dataset.query===focusedQuery:focusedProduct?n.dataset.productId===focusedProduct:n.getAttribute('href')===focusedHref);(replacement||this.input).focus({preventScroll:true});}
      this.status.textContent=state==='loading'?'Searching':`${suggestions.length} suggestions and ${products.length} products for ${query}`;
    }
    onKey(event) {
      if(event.key==='Escape'){event.preventDefault();event.stopPropagation();this.close();return;}
      if(event.key==='Tab'){
        if(this.desktop)return;
        const controls=[...this.dialog.querySelectorAll('button:not([disabled]), input:not([type="hidden"]):not([disabled]), a[href]')].filter(node=>node.getClientRects().length);
        const first=controls[0],last=controls.at(-1);
        if(event.shiftKey&&event.target===first){event.preventDefault();last?.focus();}
        else if(!event.shiftKey&&event.target===last){event.preventDefault();first?.focus();}
        return;
      }
      if(event.key!=='ArrowDown'&&event.key!=='ArrowUp')return;
      const rows=[...this.content.querySelectorAll('[data-gg-search-result]')];if(!rows.length)return;
      if(event.target!==this.input&&!rows.includes(event.target))return;
      event.preventDefault();const index=rows.indexOf(event.target),next=index+(event.key==='ArrowDown'?1:-1);
      if(next<0)this.input.focus({preventScroll:true});else rows[Math.min(next,rows.length-1)].focus();
    }
  });
}
